/**
 * 井字棋棋盘组件
 * 支持3x3、5x5、7x7三种棋盘大小
 * 美化界面设计，更大的棋盘
 */

import React from 'react';
import { TicTacToeGameState, Position, GameMode } from '../types/game';

interface TicTacToeBoardProps {
  gameState: TicTacToeGameState;
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  gameMode?: GameMode;
}

const TicTacToeBoard: React.FC<TicTacToeBoardProps> = ({
  gameState,
  onCellClick,
  disabled = false,
  gameMode = 'pvp'
}) => {
  const { board, boardSize, winningLine, lastMove } = gameState;

  // 获取格子大小 - 适中尺寸
  const getCellSize = () => {
    switch (boardSize) {
      case 3: return 'w-24 h-24 text-6xl xs:w-24 xs:h-24 xs:text-6xl sm:w-24 sm:h-24 sm:text-7xl md:w-32 md:h-32 md:text-8xl lg:w-32 lg:h-32 lg:text-9xl xl:w-40 xl:h-40 xl:text-10xl';
      case 5: return 'w-16 h-16 text-5xl xs:w-16 xs:h-16 xs:text-5xl sm:w-24 sm:h-24 sm:text-6xl md:w-24 md:h-24 md:text-7xl lg:w-32 lg:h-32 lg:text-8xl xl:w-32 xl:h-32 xl:text-8xl';
      case 7: return 'w-12 h-12 text-4xl xs:w-12 xs:h-12 xs:text-4xl sm:w-16 sm:h-16 sm:text-5xl md:w-20 md:h-20 md:text-6xl lg:w-24 lg:h-24 lg:text-7xl xl:w-24 xl:h-24 xl:text-7xl';
      default: return 'w-24 h-24 text-6xl';
    }
  };

  // 获取棋子样式 - 美化设计
  const getPieceStyle = (player: 'black' | 'white' | null, row: number, col: number) => {
    const baseStyle = `${getCellSize()} flex items-center justify-center font-bold transition-all duration-300 border-2 rounded-lg shadow-md`;

    // 检查是否是获胜连线的一部分
    const isWinningCell = winningLine?.some(pos => pos.row === row && pos.col === col);
    const isLastMove = lastMove?.row === row && lastMove?.col === col;

    let borderStyle = 'border-gray-300 hover:border-gray-400';
    if (boardSize <= 5) {
      // 为小棋盘添加更细致的边框
      if (row < boardSize - 1) borderStyle += ' border-b-4';
      if (col < boardSize - 1) borderStyle += ' border-r-4';
    }

    if (player === null) {
      return `${baseStyle} ${borderStyle} bg-gradient-to-br from-white to-gray-50 hover:from-blue-50 hover:to-blue-100 cursor-pointer hover:shadow-md`;
    }

    let bgColor = '';
    let textColor = '';
    let extraStyle = '';

    if (isWinningCell) {
      bgColor = 'bg-gradient-to-br from-green-100 to-green-200 border-green-500 shadow-lg';
      extraStyle = 'ring-4 ring-green-300 ring-opacity-50 animate-pulse';
    } else if (isLastMove) {
      bgColor = 'bg-gradient-to-br from-yellow-100 to-yellow-200 border-yellow-500';
      extraStyle = 'ring-2 ring-yellow-300 ring-opacity-50';
    } else {
      bgColor = player === 'black' ? 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-400' : 'bg-gradient-to-br from-gray-200 to-gray-300 border-gray-500';
    }

    if (player === 'black') {
      textColor = 'text-gray-900';
    } else {
      textColor = 'text-white drop-shadow-md';
    }

    return `${baseStyle} ${borderStyle} ${bgColor} ${textColor} ${extraStyle}`;
  };

  // 获取棋子符号 - 修复样式
  const getPieceSymbol = (player: 'black' | 'white' | null) => {
    if (player === null) return null;

    if (boardSize <= 3) {
      // 3x3使用更美观的X和O
      return player === 'black' ? (
        <span className="font-extrabold">✕</span>
      ) : (
        <span className="font-extrabold">○</span>
      );
    } else {
      // 5x5和7x7使用更美观的符号，继承格子大小
      return player === 'black' ? (
        <span className="font-extrabold">●</span>
      ) : (
        <span className="font-extrabold">○</span>
      );
    }
  };

  // 获取棋盘容器样式 - 紧凑适中
  const getBoardStyle = () => {
    switch (boardSize) {
      case 3: return 'grid-cols-3 gap-1 xs:gap-1.5 sm:gap-2 p-1.5 xs:p-2 sm:p-3 md:p-4 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg shadow-lg border-2 border-blue-200';
      case 5: return 'grid-cols-5 gap-0.5 xs:gap-1 sm:gap-1 p-1 xs:p-1.5 sm:p-2 md:p-3 bg-gradient-to-br from-green-100 to-blue-100 rounded-lg shadow-lg border-2 border-green-200';
      case 7: return 'grid-cols-7 gap-0.5 p-0.5 xs:p-1 sm:p-1.5 md:p-2 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-lg shadow-lg border-2 border-yellow-200';
      default: return 'grid-cols-3 gap-1 xs:gap-1.5 sm:gap-2 p-1.5 xs:p-2 sm:p-3 md:p-4 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg shadow-lg border-2 border-blue-200';
    }
  };

  // 处理格子点击
  const handleCellClick = (row: number, col: number) => {
    if (disabled || board[row][col] !== null) {
      return;
    }
    onCellClick({ row, col });
  };

  return (
    <div className="flex flex-col items-center justify-center px-1 py-1 w-full">
      {/* 棋盘容器 - 紧凑布局 */}
      <div className="flex items-center justify-center">
        <div className={`grid ${getBoardStyle()}`}>
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <button
                key={`${rowIndex}-${colIndex}`}
                className={getPieceStyle(cell, rowIndex, colIndex)}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                disabled={disabled || cell !== null}
                aria-label={`格子 ${rowIndex + 1}-${colIndex + 1}`}
              >
                {cell && (
                  <span className="select-none">
                    {getPieceSymbol(cell)}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* 图例和统计信息 - 老人友好布局 */}
      <div className="text-center space-y-1 mt-10 w-full max-w-md">
        {/* 图例 - 清晰网格 */}
        <div className="flex flex-wrap items-center justify-center gap-1 xs:gap-2 text-sm">
          <div className="flex items-center space-x-1 bg-white px-2 py-1 rounded-lg shadow-xs">
            <div className="w-3 h-3 xs:w-4 xs:h-4 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full shadow-xs"></div>
            <span className="font-medium text-gray-700 text-sm">黑方</span>
          </div>
          <div className="flex items-center space-x-1 bg-white px-2 py-1 rounded-lg shadow-xs">
            <div className="w-3 h-3 xs:w-4 xs:h-4 bg-gradient-to-br from-red-500 to-pink-500 rounded-full shadow-xs"></div>
            <span className="font-medium text-gray-700 text-sm">白方</span>
          </div>
          {lastMove && (
            <div className="flex items-center space-x-1 bg-yellow-50 px-2 py-1 rounded-lg shadow-xs border border-yellow-200">
              <div className="w-3 h-3 xs:w-4 xs:h-4 bg-gradient-to-br from-yellow-200 to-yellow-300 rounded-full"></div>
              <span className="font-medium text-gray-700 text-sm">最后</span>
            </div>
          )}
          {winningLine && winningLine.length > 0 && (
            <div className="flex items-center space-x-1 bg-green-50 px-2 py-1 rounded-lg shadow-xs border border-green-200">
              <div className="w-3 h-3 xs:w-4 xs:h-4 bg-gradient-to-br from-green-200 to-green-300 rounded-full animate-pulse"></div>
              <span className="font-medium text-gray-700 text-sm">获胜</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicTacToeBoard;